import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from '../../../../components/common/Toast/toast';
import { fetchAllBranches } from "../../../../../services/BranchService.js";
import { getScheduleLabs, getScheduleTrainers, getScheduleSlots, getLabOverlaps } from "../../../../../services/ClassroomManagementService.js";
import { useAuth } from "../../../../../context/AuthContext.js";
import { todayDateStr, minutesToTimeValue, shiftDateStr, formatDateLabel } from "../../classroomSchedule.js";
import BookingFormModal from "../BookingsTab/BookingFormModal.jsx";
import StatTile from "../../components/StatTile.jsx";
import DashboardControls from "./DashboardControls.jsx";
import SlotGrid from "./SlotGrid.jsx";
import RoomDetailModal from "./RoomDetailModal.jsx";
import TrainerDetailModal from "./TrainerDetailModal.jsx";
import StatBreakdownModal from "./StatBreakdownModal.jsx";
import ScheduleLabModal from "./ScheduleLabModal.jsx";
import { useWaypoint } from "../../../../components/common/waypoint/WaypointProvider.jsx";
import { silenceFor } from '../../../../components/common/Loader/busy';
import { BarsChart, StatusDonut, LAB_STATUS_TONE, TRAINER_STATUS_TONE } from "../../components/ClassroomCharts.jsx";
/* The page kit, in place of the classroom app's own cls-* vocabulary --
   see LabsTab for the full note. */
import "../../../../components/Trainer/TrainerDashboard.css";
import "../../../../components/common/lms-pages.css";

const EMPTY_ROOM_TILES = { total: 0, free: 0, occupied: 0, maintenance: 0 };
const EMPTY_TRAINER_TILES = { total: 0, teaching: 0, free: 0, absent: 0 };

// The booking form's weekday values, indexed by Date#getDay().
const WEEKDAY_VALUES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DEFAULT_SLOT = { startClock: "09:00", endClock: "11:00" };

// A free slot as the booking form's start/end: the slot's start, and at most
// two hours of it. A whole free day (no slot, or all 24h) starts at the usual 9-11.
const slotClocks = (slot) => {
  if (!slot || slot.endMin - slot.startMin >= 1440) return DEFAULT_SLOT;
  const end = Math.min(slot.endMin, slot.startMin + 120, 1439);
  return { startClock: minutesToTimeValue(slot.startMin), endClock: minutesToTimeValue(end) };
};

// The current time as "HH:MM", and two hours on -- held at 23:59 so a late
// evening never wraps past midnight into a To before the From.
const nowClocks = () => {
  const d = new Date();
  const from = d.getHours() * 60 + d.getMinutes();
  return {
    fromTime: minutesToTimeValue(from),
    toTime: minutesToTimeValue(Math.min(from + 120, 1439)),
  };
};

const DashboardTab = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("list-classroom", ["update"], "lms") || hasPermission("list-trainer", ["update"], "lms");
  // Scheduling from a free day creates a booking, so it takes the Bookings
  // tab's own "New booking" permission.
  const canSchedule = hasPermission("list-classroom", ["add"], "lms");
  // Editing a booking from a lab popup is the Bookings tab's own Edit.
  const canEditBooking = hasPermission("list-classroom", ["update"], "lms");

  const [loading, setLoading] = useState(true);
  // Separate from `loading` (which only ever covers the very first mount, so
  // the whole tab doesn't blank out on background refetches). `refreshing`
  // covers every *explicit* reload after that — Apply filters, or changing
  // branch/floor/status — so clicking Apply gives visible feedback in the
  // content area. The silent 60s poll deliberately does NOT set this, via
  // `isBackgroundPollRef` below, so it stays unobtrusive.
  const [refreshing, setRefreshing] = useState(false);
  const isBackgroundPollRef = useRef(false);
  // `rooms` is already the server-filtered display set (branch/floor/status/
  // search all resolved on the backend — nothing is re-filtered here). `trainerRows` stays the full, unfiltered set purely to
  // back the trainer KPI tiles (which, like the room tiles, ignore search).
  const [rooms, setRooms] = useState([]);
  const [trainerRows, setTrainerRows] = useState([]);
  const [roomTiles, setRoomTiles] = useState(EMPTY_ROOM_TILES);
  // Counted by the API (scheduleTrainers -> trainerTiles), from the same
  // unfiltered rows the trainer breakdown lists.
  const [trainerTiles, setTrainerTiles] = useState(EMPTY_TRAINER_TILES);
  // The Overview's "Labs in use by hour" series (scheduleLabs -> charts).
  const [labsByHour, setLabsByHour] = useState(null);
  const [floorOptions, setFloorOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [todaysAbsences, setTodaysAbsences] = useState([]);

  const [view, setView] = useState("overview");

  // Draft filter values — every control in the filter panel is bound to
  // one, and none of them triggers a request by itself. Only
  // `appliedFilters` feeds `load()` and the grid; it's replaced (all at
  // once) by `applyFilters` below, when the user clicks Apply.
  const [fromDate, setFromDate] = useState(todayDateStr());
  const [toDate, setToDate] = useState(todayDateStr());
  // The time filter opens on now: From is the current time, To two hours on
  // (the same two-hour default the booking form uses), so the cards answer
  // "what is free right now" and the card's Schedule has a time to book.
  const [initialClock] = useState(nowClocks);
  const [fromTime, setFromTime] = useState(initialClock.fromTime);
  const [toTime, setToTime] = useState(initialClock.toTime);
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: todayDateStr(), toDate: todayDateStr(),
    fromTime: initialClock.fromTime, toTime: initialClock.toTime, search: "",
    branch: "", floor: "", status: "", days: [], department: "",
  });

  // Branch/floor/status are drafts like the rest: they wait for Apply.
  const [branchFilter, setBranchFilter] = useState("");
  const [floorFilter, setFloorFilter] = useState("");
  // Opens on every lab: the Overview's grid is the whole branch's sheet,
  // free and busy side by side. "Free" is one pick away.
  const [statusFilter, setStatusFilter] = useState("");
  // Day-wise — which weekdays' sessions to include (e.g. only Monday/
  // Wednesday). A draft, applied with the rest.
  const [selectedDays, setSelectedDays] = useState([]);
  // Department — a searchable single-select (AsyncPagedSelect), a draft
  // applied with the rest.
  const [departmentFilter, setDepartmentFilter] = useState("");

  const [selectedRoom, setSelectedRoom] = useState(null);
  // Only set when a grid cell (not a Day-view block, not an Overview card)
  // opened the popup — it's the one date that popup's "sessions" list is
  // actually scoped to, overriding the general scheduleLabel/rangeNote below.
  const [selectedRoomDate, setSelectedRoomDate] = useState(null);
  const [selectedTrainerRow, setSelectedTrainerRow] = useState(null);
  const [selectedTrainerDate, setSelectedTrainerDate] = useState(null);
  // The booking form opened from a free day, pre-pointed at that lab or
  // trainer, date and slot -- or null.
  const [scheduling, setScheduling] = useState(null);
  // A free cell of the lab grid that was clicked -- { room, when } for the
  // quick booking popup.
  const [labToSchedule, setLabToSchedule] = useState(null);
  // The booking a lab popup's Edit opened -- the booking form, over the popup.
  const [editingBooking, setEditingBooking] = useState(null);

  const filtersDirty =
    fromDate !== appliedFilters.fromDate ||
    toDate !== appliedFilters.toDate ||
    fromTime !== appliedFilters.fromTime ||
    toTime !== appliedFilters.toTime ||
    search !== appliedFilters.search ||
    branchFilter !== appliedFilters.branch ||
    floorFilter !== appliedFilters.floor ||
    statusFilter !== appliedFilters.status ||
    selectedDays.join(",") !== appliedFilters.days.join(",") ||
    departmentFilter !== appliedFilters.department;

  // The Overview grid's day: the applied From date, stepped within the
  // applied range by its own Prev / Next.
  const [overviewDay, setOverviewDay] = useState(todayDateStr);

  const applyFilters = useCallback((e) => {
    e?.preventDefault?.();
    setAppliedFilters({
      fromDate, toDate, fromTime, toTime, search,
      branch: branchFilter, floor: floorFilter, status: statusFilter, days: selectedDays, department: departmentFilter,
    });
    setOverviewDay(fromDate);
  }, [fromDate, toDate, fromTime, toTime, search, branchFilter, floorFilter, statusFilter, selectedDays, departmentFilter]);

  // Branch list only backs the filter dropdown's options — it doesn't depend on
  // date/time/search, so it's fetched once on mount, not on every reload.
  const loadBranches = useCallback(async () => {
    try {
      setBranches((await fetchAllBranches()) || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  }, []);

  // Guards against two `load()` calls racing (React 18 StrictMode fires this
  // effect twice in dev, and a filter change can also fire a new load before
  // the previous one lands): each call stamps its own id, and the one
  // response that's still current when it resolves is the only one allowed
  // to touch state. Without this, a still-running *older* call finishing
  // (or failing/timing out) after a newer one already rendered good data
  // would silently blank the screen with stale results — which is exactly
  // what "second request succeeds but the UI shows nothing" looks like.
  const latestRequestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;
    const isCurrent = () => latestRequestIdRef.current === requestId;

    const isBackgroundPoll = isBackgroundPollRef.current;
    isBackgroundPollRef.current = false;
    if (!isBackgroundPoll) setRefreshing(true);
    try {
      const {
        fromDate: aFromDate, toDate: aToDate, fromTime: aFromTime, toTime: aToTime, search: aSearch,
        branch: aBranch, floor: aFloor, status: aStatus, days: aDays, department: aDepartment,
      } = appliedFilters;
      const todayStr = todayDateStr();
      const isToday = aFromDate === todayStr && aToDate === todayStr;

      // Labs and Trainers are two separate API calls (each server-side
      // building only its own half — see classroommanagement.service.js),
      // fetched in parallel. Both are required for a meaningful page (the
      // KPI tile row up top shows lab AND trainer counts on every view), so
      // either one rejecting fails the load; the "today" snapshot is only
      // there to back the absences banner, so it alone stays optional
      // (Promise.allSettled lets a slow/failed snapshot never take the rest
      // down with it).
      const [labsResult, trainersResult, todayResult] = await Promise.allSettled([
        getScheduleLabs({
          fromDate: aFromDate, toDate: aToDate, fromTime: aFromTime, toTime: aToTime, days: aDays, department: aDepartment,
          branch: aBranch, floor: aFloor, status: aStatus, search: aSearch,
        }),
        getScheduleTrainers({
          fromDate: aFromDate, toDate: aToDate, fromTime: aFromTime, toTime: aToTime, days: aDays, department: aDepartment, search: aSearch,
        }),
        isToday ? Promise.resolve(null) : getScheduleTrainers({ fromDate: todayStr, toDate: todayStr }),
      ]);
      if (!isCurrent()) return; // a newer load() has since started — drop this result

      if (labsResult.status === "rejected") throw labsResult.reason;
      if (trainersResult.status === "rejected") throw trainersResult.reason;
      const labs = labsResult.value;
      const trainersData = trainersResult.value;
      const todaySchedule = todayResult.status === "fulfilled" ? todayResult.value : null;

      setRooms(labs?.rooms || []);
      setTrainerRows(trainersData?.trainers || []);
      setTrainerTiles(trainersData?.trainerTiles || EMPTY_TRAINER_TILES);
      setRoomTiles(labs?.roomTiles || EMPTY_ROOM_TILES);
      setLabsByHour(labs?.charts?.labsByHour || null);
      setFloorOptions(labs?.floorOptions || []);

      const trainersToday = isToday ? trainersData?.trainers : todaySchedule?.trainers;
      setTodaysAbsences(
        (trainersToday || [])
          .filter((row) => row.status === "absent" && row.leave)
          .map((row) => ({ id: row.leave.id, trainerName: row.trainer.trainerName, reason: row.leave.reason }))
      );
    } catch (error) {
      if (!isCurrent()) return; // superseded — don't blank a newer, already-loaded result
      console.error("Error loading classroom dashboard:", error);
      toast.error("Failed to load classroom dashboard data");
      // Don't leave the previous filter's cards on screen — they don't belong
      // to the filters currently applied, and silently showing them would
      // look like a (wrong) result for the failed request instead of a
      // failure. Tiles/floor options are left alone since clearing them would
      // also wipe the dropdown a user might need to fix the filter that failed.
      setRooms([]);
      setTrainerRows([]);
      setTrainerTiles(EMPTY_TRAINER_TILES);
      setLabsByHour(null);
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [appliedFilters]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      isBackgroundPollRef.current = true;
      /* Nobody asked for this refresh, so it must not raise the global
         overlay -- a veil over the app once a minute reads as the app
         hanging. Time-boxed rather than a flag, because load() fans out
         to three services and none of their signatures carries one. */
      silenceFor(4000);
      load();
    }, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // ── The day grid: the Overview and both timelines ─────────────────
  // One day, half-hour slots down; labs across on the Overview and the Labs
  // timeline, trainers across on the Trainers timeline. Fetched on its own
  // from GET /schedule/slots, which resolves every column and cell. The
  // Overview's day starts on the applied From date and steps within the
  // applied range; the timelines step through days with their own Prev / Next.
  const [timelineDay, setTimelineDay] = useState(todayDateStr);
  // Bumped after a popup changes something (a booking, leave, out of
  // order), so the grid refetches as the tiles do.
  const [timelineReload, setTimelineReload] = useState(0);
  const [slotGrid, setSlotGrid] = useState(null);
  const [slotGridLoading, setSlotGridLoading] = useState(false);
  const [slotGridFailed, setSlotGridFailed] = useState(false);
  const slotGridSeq = useRef(0);
  const daysKey = appliedFilters.days.join(",");
  const timelineKind = view === "labs" || view === "trainers" ? view : null;
  const gridKind = view === "trainers" ? "trainers" : "labs";
  const gridDate = timelineKind ? timelineDay : overviewDay;

  useEffect(() => {
    const mine = ++slotGridSeq.current;
    setSlotGridLoading(true);
    setSlotGridFailed(false);
    // The grid draws its own skeleton, so the global overlay stays down.
    silenceFor();
    getScheduleSlots({
      kind: gridKind,
      date: gridDate,
      fromTime: appliedFilters.fromTime,
      toTime: appliedFilters.toTime,
      days: daysKey ? daysKey.split(",") : [],
      department: appliedFilters.department,
      branch: appliedFilters.branch,
      floor: appliedFilters.floor,
      status: appliedFilters.status,
      search: appliedFilters.search,
    })
      .then((result) => {
        if (mine === slotGridSeq.current) setSlotGrid(result);
      })
      .catch(() => {
        if (mine === slotGridSeq.current) {
          setSlotGridFailed(true);
          setSlotGrid(null);
        }
      })
      .finally(() => {
        if (mine === slotGridSeq.current) setSlotGridLoading(false);
      });
  }, [
    gridKind, gridDate, appliedFilters.fromTime, appliedFilters.toTime, appliedFilters.search,
    daysKey, appliedFilters.department, appliedFilters.branch, appliedFilters.floor, appliedFilters.status, timelineReload,
  ]);

  // Overlaps already saved: running batches holding one lab at the same
  // time, from before the batch save refused them. Not tied to the
  // filters -- every one still to come, so none goes unseen.
  const [labOverlaps, setLabOverlaps] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getLabOverlaps()
      .then((result) => { if (!cancelled) setLabOverlaps(result?.overlaps || []); })
      .catch(() => { if (!cancelled) setLabOverlaps([]); });
    return () => { cancelled = true; };
  }, [timelineReload]);

  // A popup opened from the grid stays up while a booking in it is edited;
  // when the grid comes back after the save, the popup takes that lab's or
  // trainer's fresh row so it shows the change.
  useEffect(() => {
    if (!slotGrid) return;
    const freshRow = (key) => slotGrid.columns.find((c) => c.key === key)?.row;
    if (slotGrid.kind === "trainers") {
      setSelectedTrainerRow((current) =>
        !current || selectedTrainerDate !== slotGrid.date ? current : freshRow(current.trainer.id) || current
      );
    } else {
      setSelectedRoom((current) =>
        !current || selectedRoomDate !== slotGrid.date ? current : freshRow(current.classroom.id) || current
      );
    }
  }, [slotGrid, selectedRoomDate, selectedTrainerDate]);

  const reloadAll = useCallback(() => {
    load();
    setTimelineReload((n) => n + 1);
  }, [load]);

  // A batch edited from its popup (Waypoint's "Edit batch") may have moved
  // lab, days or time, so the tiles, grid and overlaps load again.
  useEffect(() => {
    const onSaved = (e) => { if (e.detail?.type === "batch") reloadAll(); };
    window.addEventListener("waypoint:saved", onSaved);
    return () => window.removeEventListener("waypoint:saved", onSaved);
  }, [reloadAll]);

  // Opening a room/trainer from a normal click (Overview card, or a Day-view
  // timeline block) always shows the applied range's own anchor-day data —
  // clear any leftover single-date override from a previous grid-cell open.
  // `rooms`/`trainerRows` now come from the Labs/Trainers-only endpoints
  // (see ClassroomManagementService.js), which don't carry the raw `sessions`
  // field at all — fall back to `rangeSessions` so the detail modal always
  // has a real array to read, never undefined.
  const openRoom = useCallback((room) => {
    setSelectedRoom(room.sessions ? room : { ...room, sessions: room.rangeSessions || [] });
    setSelectedRoomDate(null);
  }, []);

  // A column heading or a filled cell of the grid: that lab's or trainer's
  // popup for the grid's day, from the row the API sent with the column.
  const openGridColumn = useCallback((row) => {
    if (!slotGrid) return;
    if (slotGrid.kind === "trainers") {
      setSelectedTrainerRow(row);
      setSelectedTrainerDate(slotGrid.date);
    } else {
      setSelectedRoom(row);
      setSelectedRoomDate(slotGrid.date);
    }
  }, [slotGrid]);

  // A free cell, on that day, from that slot to the end of the free time it
  // sits in (at most two hours -- slotClocks): a lab's opens the quick
  // booking popup, a trainer's the booking form pointed at that trainer.
  const scheduleGridSlot = useCallback((row, slot) => {
    if (!slotGrid) return;
    const { date, kind } = slotGrid;
    const free = (row.freeWindows || []).find((w) => w.startMin <= slot.startMin && slot.startMin < w.endMin);
    const { startClock, endClock } = slotClocks({ startMin: slot.startMin, endMin: free ? free.endMin : slot.endMin });
    const weekday = WEEKDAY_VALUES[new Date(`${date}T00:00:00`).getDay()];
    if (kind === "trainers") {
      setScheduling({
        date,
        days: [weekday],
        trainer: { value: row.trainer.id, label: row.trainer.trainerName || row.trainer.trainerEmail || row.trainer.id },
        startClock,
        endClock,
      });
      return;
    }
    setLabToSchedule({
      room: row,
      when: { fromDate: date, toDate: date, fromTime: startClock, toTime: endClock, days: [weekday] },
    });
  }, [slotGrid]);

  // ── The six tiles open what they counted ─────────────────────────
  // A tile is a count, not a record, so it opens StatBreakdownModal --
  // the list behind the number -- and every trainer and batch in that
  // list opens through Waypoint (instruction_ui.json, "waypoint").
  const { open: openEntity } = useWaypoint();
  const [breakdown, setBreakdown] = useState(null);
  const breakdownRequestRef = useRef(0);

  const openBreakdown = useCallback(async (kind, status, label) => {
    const requestId = ++breakdownRequestRef.current;
    const pick = (list) => (list || []).filter((r) => r.status === status);

    if (kind === "overlap") {
      setBreakdown({ kind, status, label, rows: labOverlaps, loading: false, error: null });
      return;
    }

    // trainerRows is the unfiltered set the trainer tiles count, so the
    // list is exactly the rows behind the number.
    if (kind === "trainer") {
      setBreakdown({ kind, status, label, rows: pick(trainerRows), loading: false, error: null });
      return;
    }

    // The room tiles are counted server-side BEFORE branch/floor/status/
    // search (classroommanagement.service.js, buildRoomRows), but `rooms`
    // arrives with all four applied. With none on, the two are the same
    // set; with any on, fetch the snapshot the tiles counted, or a tile
    // reading 7 opens onto a list of 2.
    const roomFiltersOn = Boolean(appliedFilters.branch || appliedFilters.floor || appliedFilters.status || appliedFilters.search);
    if (!roomFiltersOn) {
      setBreakdown({ kind, status, label, rows: pick(rooms), loading: false, error: null });
      return;
    }

    setBreakdown({ kind, status, label, rows: [], loading: true, error: null });
    try {
      const { fromDate: aFrom, toDate: aTo, fromTime: aFromTime, toTime: aToTime } = appliedFilters;
      const labs = await getScheduleLabs({
        fromDate: aFrom, toDate: aTo, fromTime: aFromTime, toTime: aToTime, days: appliedFilters.days, department: appliedFilters.department,
      });
      if (breakdownRequestRef.current !== requestId) return;
      setBreakdown({ kind, status, label, rows: pick(labs?.rooms), loading: false, error: null });
    } catch (error) {
      if (breakdownRequestRef.current !== requestId) return;
      console.error("Error loading the labs behind a tile:", error);
      setBreakdown({ kind, status, label, rows: [], loading: false, error: "Could not load this list. Close it and try again." });
    }
  }, [trainerRows, rooms, appliedFilters, labOverlaps]);

  const closeBreakdown = useCallback(() => {
    // A list still loading must not reopen the popup once it lands.
    breakdownRequestRef.current += 1;
    setBreakdown(null);
  }, []);

  // A page opening a Waypoint from inside its own modal closes that modal
  // first -- two stacked popups is never what is wanted, and Waypoint's
  // own back link is what returns the reader.
  const bridgeFromBreakdown = useCallback((type, id) => {
    if (!id) return;
    closeBreakdown();
    openEntity(type, id);
  }, [closeBreakdown, openEntity]);

  // ── Out of the day popups: schedule a free day, or open a batch ────
  // Each closes its popup first -- two stacked modals is never wanted.
  const scheduleRoom = useCallback((slot) => {
    const room = selectedRoom;
    if (!room?.classroom) return;
    const date = selectedRoomDate || appliedFilters.fromDate;
    setSelectedRoom(null);
    setSelectedRoomDate(null);
    setScheduling({
      date,
      days: [WEEKDAY_VALUES[new Date(`${date}T00:00:00`).getDay()]],
      classroom: { value: room.classroom.id, label: room.classroom.classroomNo },
      ...slotClocks(slot),
    });
  }, [selectedRoom, selectedRoomDate, appliedFilters.fromDate]);

  const scheduleTrainer = useCallback(() => {
    const row = selectedTrainerRow;
    if (!row?.trainer) return;
    const date = selectedTrainerDate || appliedFilters.fromDate;
    setSelectedTrainerRow(null);
    setSelectedTrainerDate(null);
    setScheduling({
      date,
      days: [WEEKDAY_VALUES[new Date(`${date}T00:00:00`).getDay()]],
      trainer: { value: row.trainer.id, label: row.trainer.trainerName || row.trainer.trainerEmail || row.trainer.id },
      ...DEFAULT_SLOT,
    });
  }, [selectedTrainerRow, selectedTrainerDate, appliedFilters.fromDate]);

  const openBatchFromRoom = useCallback((batchId) => {
    setSelectedRoom(null);
    setSelectedRoomDate(null);
    openEntity("batch", batchId);
  }, [openEntity]);

  const openBatchFromTrainer = useCallback((batchId) => {
    setSelectedTrainerRow(null);
    setSelectedTrainerDate(null);
    openEntity("batch", batchId);
  }, [openEntity]);

  const openRoomFromBreakdown = useCallback((room) => {
    closeBreakdown();
    openRoom(room);
  }, [closeBreakdown, openRoom]);

  const { teaching: teachingCount, free: freeTrainerCount, absent: absentCount } = trainerTiles;

  // ── The Overview's charts ─────────────────────────────────────────
  // The doughnuts are the tile figures above them, as proportions; the
  // hourly bars arrive worked out (scheduleLabs -> charts.labsByHour).
  const labSegments = useMemo(
    () => [
      { label: "Free", value: roomTiles.free, color: LAB_STATUS_TONE.free },
      { label: "Occupied", value: roomTiles.occupied, color: LAB_STATUS_TONE.occupied },
      { label: "Out of order", value: roomTiles.maintenance, color: LAB_STATUS_TONE.maintenance },
    ],
    [roomTiles]
  );

  const trainerSegments = useMemo(
    () => [
      { label: "Teaching", value: trainerTiles.teaching, color: TRAINER_STATUS_TONE.teaching },
      { label: "Free", value: trainerTiles.free, color: TRAINER_STATUS_TONE.free },
      { label: "Absent", value: trainerTiles.absent, color: TRAINER_STATUS_TONE.absent },
    ],
    [trainerTiles]
  );

  const hourBars = useMemo(
    () =>
      (labsByHour?.hours || []).map((h) => ({
        label: h.label,
        value: h.inUse,
        tip: labsByHour.perDay
          ? ` ${h.inUse} labs in use on an average day`
          : ` ${h.inUse} ${h.inUse === 1 ? "lab" : "labs"} in use`,
      })),
    [labsByHour]
  );

  if (loading) {
    return (
      <div className="pg">
        <div className="pg-state">
          <span className="pg-spinner" role="status" aria-hidden="true" />
          <p className="pg-state-text">Loading the dashboard...</p>
        </div>
      </div>
    );
  }

  // The room/trainer detail modals always list sessions for the applied
  // `fromDate` alone (multi-day timelines don't fit their single-day layout),
  // while status/shifted/absent reflect the whole applied range — so when
  // that range spans more than one day, spell it out under the "schedule"
  // table. These read `appliedFilters` (what was actually fetched), not the
  // possibly-unapplied draft values in the controls above.
  const { fromDate: appliedFromDate, toDate: appliedToDate, fromTime: appliedFromTime, toTime: appliedToTime } = appliedFilters;
  const isRange = appliedFromDate !== appliedToDate;
  const timeRangeLabel = appliedFromTime && appliedToTime && appliedFromTime !== appliedToTime
    ? ` between ${appliedFromTime}–${appliedToTime}`
    : appliedFromTime ? ` at ${appliedFromTime}` : "";
  const scheduleLabel = appliedFromDate === todayDateStr() && !isRange ? "Today's schedule" : `Schedule for ${appliedFromDate}`;
  const rangeNote = isRange
    ? `Status reflects the full ${appliedFromDate} – ${appliedToDate} range${timeRangeLabel}; sessions below are for ${appliedFromDate} only.`
    : null;

  // A grid-cell-opened popup is always showing one real, specific day's
  // actual sessions (not an anchor-day stand-in for a wider range), so it
  // gets its own label off that date and never needs the rangeNote caveat.
  const dateLabel = (d) => (d === todayDateStr() ? "Today's schedule" : `Schedule for ${d}`);
  const roomModalLabel = selectedRoomDate ? dateLabel(selectedRoomDate) : scheduleLabel;
  const roomModalNote = selectedRoomDate ? null : rangeNote;
  const trainerModalLabel = selectedTrainerDate ? dateLabel(selectedTrainerDate) : scheduleLabel;
  const trainerModalNote = selectedTrainerDate ? null : rangeNote;

  return (
    <div className="pg">
      <header className="pg-head">
        <div className="pg-head-main">
          <h1 className="pg-title">Classroom dashboard</h1>
          <p className="pg-sub">
            Which rooms and trainers are busy, free, or unavailable right now.
          </p>
        </div>
      </header>

      {/* Each figure opens the labs or trainers it counted, and every
          trainer and batch in that list opens through Waypoint. A zero
          stays a plain figure rather than opening an empty list. */}
      <div className="br-stats">
        {[
          { kind: "room", status: "free", num: roomTiles.free, label: "Labs free", variant: "free" },
          { kind: "room", status: "occupied", num: roomTiles.occupied, label: "Labs occupied" },
          { kind: "room", status: "maintenance", num: roomTiles.maintenance, label: "Out of order", variant: "crit" },
          { kind: "trainer", status: "teaching", num: teachingCount, label: "Trainers teaching" },
          { kind: "trainer", status: "free", num: freeTrainerCount, label: "Trainers free", variant: "free" },
          { kind: "trainer", status: "absent", num: absentCount, label: "Trainers absent", variant: "crit" },
          { kind: "overlap", status: "overlap", num: labOverlaps.length, label: "Lab overlaps", variant: labOverlaps.length ? "crit" : undefined },
        ].map((tile) => (
          <StatTile
            key={tile.label}
            num={tile.num}
            label={tile.label}
            variant={tile.variant}
            onClick={tile.num > 0 ? () => openBreakdown(tile.kind, tile.status, tile.label) : undefined}
          />
        ))}
      </div>

      {todaysAbsences.length > 0 && (
        <div className="cls-alerts">
          {todaysAbsences.map((l) => (
            <p key={l.id} className="att-note att-note--warn">
              {l.trainerName} is on leave today &mdash; {l.reason}
            </p>
          ))}
        </div>
      )}

      {/* The same snapshot as the tiles, as charts: every lab and every
          trainer for the dates and times applied -- above the filters. */}
      {view === "overview" && (
        <div className="br-charts">
          <StatusDonut
            className="br-chart--third"
            title="Labs"
            sub="Every lab, for the dates and times applied."
            segments={labSegments}
            centreValue={roomTiles.total}
            centreLabel="labs"
            empty="No labs on record."
          />
          <StatusDonut
            className="br-chart--third"
            title="Trainers"
            sub="Every trainer, for the dates and times applied."
            segments={trainerSegments}
            centreValue={trainerTiles.total}
            centreLabel="trainers"
            empty="No trainers on record."
          />
          <BarsChart
            className="br-chart--third"
            title="Labs in use by hour"
            sub={
              labsByHour?.peak
                ? `Busiest at ${labsByHour.peak.label}: ${labsByHour.peak.inUse} of ${labsByHour.labs} labs${labsByHour.perDay ? " on an average day" : ""}.`
                : null
            }
            bars={hourBars}
            empty="No lab is booked for the dates and times applied."
          />
        </div>
      )}

      <DashboardControls
        view={view}
        onViewChange={setView}
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        fromTime={fromTime}
        onFromTimeChange={setFromTime}
        toTime={toTime}
        onToTimeChange={setToTime}
        days={selectedDays}
        onDaysChange={setSelectedDays}
        department={departmentFilter}
        onDepartmentChange={setDepartmentFilter}
        branch={branchFilter}
        onBranchChange={setBranchFilter}
        branchOptions={branches}
        floor={floorFilter}
        onFloorChange={setFloorFilter}
        floorOptions={floorOptions}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        search={search}
        onSearchChange={setSearch}
        onApply={applyFilters}
        applyDirty={filtersDirty}
      />

      {/* What the colours on the timelines mean, said once. */}
      <div className="cls-legend">
        <span className="pg-pill pg-pill--good">Free</span>
        <span className="pg-pill pg-pill--live">Occupied</span>
        <span className="pg-pill pg-pill--bad">Out of order</span>
        <span className="pg-pill pg-pill--warn">Shifted</span>
        <span className="pg-pill pg-pill--warn">Two batches in one lab</span>
        <span className="pg-pill pg-pill--bad">Absent</span>
      </div>

      {/* The Labs / Trainers timelines: one day at a time, stepped here. */}
      {timelineKind && (
        <>
          <div className="cls-month-bar">
            <button
              type="button"
              className="pg-btn"
              onClick={() => setTimelineDay((d) => shiftDateStr(d, -1))}
              aria-label="Previous day"
            >
              ‹ Prev
            </button>
            <h2 className="cls-month-title">
              {formatDateLabel(timelineDay, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
            </h2>
            <button
              type="button"
              className="pg-btn"
              onClick={() => setTimelineDay((d) => shiftDateStr(d, 1))}
              aria-label="Next day"
            >
              Next ›
            </button>

            <div className="cls-month-tools">
              <label className="pg-label" htmlFor="cls-day-pick">Day</label>
              <input
                id="cls-day-pick"
                type="date"
                className="pg-input"
                value={timelineDay}
                onChange={(e) => e.target.value && setTimelineDay(e.target.value)}
              />
              <button
                type="button"
                className="pg-btn"
                onClick={() => setTimelineDay(todayDateStr())}
                disabled={timelineDay === todayDateStr()}
              >
                Today
              </button>
            </div>
          </div>

          <SlotGrid
            grid={slotGrid}
            loading={slotGridLoading}
            failed={slotGridFailed}
            onColumnOpen={openGridColumn}
            onFreeSlot={canSchedule ? scheduleGridSlot : undefined}
          />
        </>
      )}

      {/* A From-To range on the Overview: the grid is one day, so it steps
          through the range's days here, never past either end. */}
      {view === "overview" && appliedFilters.fromDate !== appliedFilters.toDate && (
        <div className="cls-month-bar">
          <button
            type="button"
            className="pg-btn"
            onClick={() => setOverviewDay((d) => shiftDateStr(d, -1))}
            disabled={overviewDay <= appliedFilters.fromDate}
            aria-label="Previous day"
          >
            ‹ Prev
          </button>
          <h2 className="cls-month-title">
            {formatDateLabel(overviewDay, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
          </h2>
          <button
            type="button"
            className="pg-btn"
            onClick={() => setOverviewDay((d) => shiftDateStr(d, 1))}
            disabled={overviewDay >= appliedFilters.toDate}
            aria-label="Next day"
          >
            Next ›
          </button>
        </div>
      )}

      {view === "overview" && (refreshing ? (
        <div className="pg-state">
          <span className="pg-spinner" role="status" aria-hidden="true" />
          <p className="pg-state-text">Loading the schedule...</p>
        </div>
      ) : (
        <SlotGrid
          grid={slotGrid}
          loading={slotGridLoading}
          failed={slotGridFailed}
          showDate
          onColumnOpen={openGridColumn}
          onFreeSlot={canSchedule ? scheduleGridSlot : undefined}
        />
      ))}

      {selectedRoom && (
        <RoomDetailModal
          room={selectedRoom}
          canEdit={canEdit}
          scheduleLabel={roomModalLabel}
          rangeNote={roomModalNote}
          onClose={() => { setSelectedRoom(null); setSelectedRoomDate(null); }}
          onChanged={reloadAll}
          onSchedule={canSchedule ? scheduleRoom : undefined}
          onOpenBatch={openBatchFromRoom}
          onEditBooking={canEditBooking ? setEditingBooking : undefined}
        />
      )}
      {selectedTrainerRow && (
        <TrainerDetailModal
          row={selectedTrainerRow}
          canEdit={canEdit}
          todayStr={selectedTrainerDate || todayDateStr()}
          scheduleLabel={trainerModalLabel}
          rangeNote={trainerModalNote}
          onClose={() => { setSelectedTrainerRow(null); setSelectedTrainerDate(null); }}
          onChanged={reloadAll}
          onSchedule={canSchedule ? scheduleTrainer : undefined}
          onOpenBatch={openBatchFromTrainer}
          onEditBooking={canEditBooking ? setEditingBooking : undefined}
        />
      )}
      {editingBooking && (
        <BookingFormModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          // Refresh only: the form shows its done panel and closes itself.
          onSaved={reloadAll}
        />
      )}
      {scheduling && (
        <BookingFormModal
          booking={null}
          prefill={scheduling}
          onClose={() => setScheduling(null)}
          onSaved={reloadAll}
        />
      )}
      {/* A free cell of the lab grid. Its fields start from that day and
          slot, and can be changed in the popup. */}
      {labToSchedule && (
        <ScheduleLabModal
          room={labToSchedule.room}
          when={labToSchedule.when}
          onClose={() => setLabToSchedule(null)}
          onSaved={reloadAll}
        />
      )}
      {breakdown && (
        <StatBreakdownModal
          breakdown={breakdown}
          onClose={closeBreakdown}
          onOpenEntity={bridgeFromBreakdown}
          onOpenRoom={openRoomFromBreakdown}
        />
      )}
    </div>
  );
};

export default DashboardTab;
